import { Controller, ControllerProps, FieldValues } from "react-hook-form";
import { Text } from "react-native";
import { TextAreaInput } from "ui/atoms/input/TextArea";

import { View } from "ui/atoms/view";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from "ui/gluestack/form-control";
import { AlertCircleIcon } from "ui/gluestack/icon";
import { requiredValidation } from "ui/utils/Validators";

export interface Props<TFieldValues extends FieldValues = FieldValues> {
  id: string;
  name: ControllerProps<TFieldValues>["name"];
  label?: string;
  placeholder?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  control: ControllerProps<TFieldValues>["control"];
  rules?: ControllerProps<TFieldValues>["rules"];
  className?: string;
}

export function FormControlledInputTextArea<
  TFieldValues extends FieldValues = FieldValues,
>({
  id,
  name,
  isReadOnly,
  isDisabled,
  isRequired,
  placeholder,
  label,
  control,
  rules,
  className,
}: Props<TFieldValues>) {
  const localRules = {
    required: isRequired ? requiredValidation(label ?? "Field") : undefined,
    ...rules,
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ fieldState: { error }, field: { value, onChange } }) => (
        <FormControl
          isInvalid={!!error}
          size="md"
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          isRequired={isRequired}
        >
          {label ? (
            <Text className={`test-sm font-medium mb-2 ${className}`}>
              {label}
              {!isRequired ? " (Optional)" : ""}
            </Text>
          ) : null}
          <View className={`relative my-1 fill-white`}>
            <TextAreaInput
              id={id}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              isDisabled={isDisabled}
              name={name}
            />
          </View>
          {error?.message ? (
            <FormControlError>
              <FormControlErrorIcon
                as={AlertCircleIcon}
                className="text-error-500"
                size="sm"
              />
              <FormControlErrorText className="text-error-500 text-sm">
                {error.message}
              </FormControlErrorText>
            </FormControlError>
          ) : null}
        </FormControl>
      )}
      rules={localRules}
    />
  );
}
